import React, { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { Blend, Link2, Link2Off } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { capitalizeFirstLetter } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { IntegrationServices } from '@/modules/common/types/integrations';

import { BaseOnboardingEntity } from '../../types/base-onboarding-entity';
import {
  getCommonValues,
  getEntitiesWithPersonLabel,
  getIntegrationData,
} from '../../utils/reconciliation-helpers';
import { OnboardingConflictsComparisonTable } from './onboarding-conflicts-comparison-table';
import {
  IntegrationConnectionStatus,
  MergeRequestBuilder,
  ONBOARDING_CONFLICT_CATEGORY_ATTRIBUTES,
  ONBOARDING_CONFLICT_CATEGORY_INTEGRATIONS,
  OnboardingConflictRow,
  OnboardingResolveConflictAttributeRow,
} from './types';

interface ResolveDuplicatesModalProps<TEntity extends BaseOnboardingEntity> {
  selectedItems: TEntity[];
  onSuccess: () => void;
  /** Attribute rows to display (name, email, client, etc.) */
  attributeRows: OnboardingResolveConflictAttributeRow<TEntity>[];
  /** List of integration enum values for this entity */
  integrationsList: IntegrationServices[];
  /** API function to call for merging */
  mergeMutationFn: (request: any) => Promise<void>;
  /** Cache invalidation after successful merge */
  invalidateQueries: (queryClient: QueryClient) => Promise<void>;
  /** Build the merge request from form data */
  mergeRequestBuilder: MergeRequestBuilder<TEntity>;
  /** i18n namespace for this entity (e.g. 'authenticated.onboarding.team') */
  i18nNamespace: string;
  /** i18n key for the "Person N" labels */
  personLabelI18nKey: string;
  /** i18n key for the "Resulting Entity" column header (e.g. 'authenticated.onboarding.team:duplicates.resultingUser') */
  resultingEntityI18nKey: string;
  /** Optional footer notice (e.g. clients' "missing integration" warning) */
  renderFooterNotice?: () => React.ReactNode;
  triggerDisabled?: boolean;
  triggerDisabledTooltip?: string;
}

/**
 * Builds a Zod schema dynamically from attribute rows and integrations.
 */
function buildSchema<TEntity extends BaseOnboardingEntity>(
  attributeRows: OnboardingResolveConflictAttributeRow<TEntity>[],
  integrationsList: IntegrationServices[],
  requiredMessage: string,
  selectedItems: TEntity[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const row of attributeRows) {
    const hasOptions = row.getSelectOptions(selectedItems).length > 0;

    if (row.required !== false) {
      shape[row.key] = row.zodSchema ?? z.string().min(1, requiredMessage);
    } else if (hasOptions) {
      // Field is marked optional but has selectable options — require a selection
      shape[row.key] = z.string().min(1, requiredMessage);
    } else {
      shape[row.key] = row.zodSchema ?? z.string().optional();
    }
  }

  for (const integration of integrationsList) {
    shape[integration] = z.string().min(1, requiredMessage);
  }

  return z.object(shape);
}

/**
 * Builds default form values from attribute rows and integration defaults.
 */
function buildDefaultValues<TEntity extends BaseOnboardingEntity>(
  attributeRows: OnboardingResolveConflictAttributeRow<TEntity>[],
  integrationsList: IntegrationServices[]
): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const row of attributeRows) {
    defaults[row.key] = '';
  }
  for (const integration of integrationsList) {
    defaults[integration] = '';
  }
  return defaults;
}

/**
 * Builds OnboardingConflictRow[] from attribute rows configuration and selected items.
 */
function buildAttributeTableRows<TEntity extends BaseOnboardingEntity>(
  attributeRows: OnboardingResolveConflictAttributeRow<TEntity>[],
  selectedItems: TEntity[]
): OnboardingConflictRow[] {
  return attributeRows.map(row => ({
    fieldKey: row.key,
    fieldLabel: row.label,
    isIntegrationRow: false,
    selectOptions: row.getSelectOptions(selectedItems).map(opt => ({
      value: opt.value,
      label: opt.label,
      customContent: opt.customContent,
    })),
    placeholder: row.placeholder,
    personValues: selectedItems.map(item => {
      const isEmpty = row.isEmpty?.(item) ?? false;
      return {
        displayValue: row.getDisplayValue(item),
        isDestructive: isEmpty && !row.showMutedWhenEmpty,
        isMuted: isEmpty && !!row.showMutedWhenEmpty,
      };
    }),
    selectDisabled: row.selectDisabled,
    selectDisabledTooltip: row.selectDisabledTooltip,
  }));
}

/**
 * Builds OnboardingConflictRow[] from integration data for the integrations group.
 */
function buildIntegrationTableRows(
  integrationsList: IntegrationServices[],
  integrationDataResult: ReturnType<typeof getIntegrationData>,
  i18nNamespace: string,
  t: (key: string) => string
): OnboardingConflictRow[] {
  return integrationsList.map(integration => {
    const integrationOptions = integrationDataResult.options[integration] ?? [];

    return {
      fieldKey: integration,
      fieldLabel: capitalizeFirstLetter(integration),
      isIntegrationRow: true,
      selectOptions: integrationOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
      })),
      placeholder: t(`${i18nNamespace}:duplicates.selectIntegration`),
      personValues: (integrationDataResult.cells[integration] ?? []).map(cellData => ({
        displayValue:
          cellData.status === IntegrationConnectionStatus.Connected ? (
            <Link2 className="h-5 w-5 text-success" />
          ) : (
            <Link2Off className="h-5 w-5 text-muted-foreground" />
          ),
        isDestructive: false,
      })),
    };
  });
}

export function ResolveOnboardingConflictsModal<TEntity extends BaseOnboardingEntity>({
  selectedItems,
  onSuccess,
  attributeRows,
  integrationsList,
  mergeMutationFn,
  invalidateQueries,
  mergeRequestBuilder,
  i18nNamespace,
  personLabelI18nKey,
  resultingEntityI18nKey,
  renderFooterNotice,
  triggerDisabled = false,
  triggerDisabledTooltip,
}: ResolveDuplicatesModalProps<TEntity>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const requiredMessage = t(`${i18nNamespace}:validation.field.required`);
  const schema = useMemo(
    () => buildSchema(attributeRows, integrationsList, requiredMessage, selectedItems),
    [attributeRows, integrationsList, requiredMessage, selectedItems]
  );

  type FormValues = z.infer<typeof schema>;

  const commonValues = useMemo(() => {
    // Use deep equality for nested objects (e.g. project.client)
    return getCommonValues(selectedItems, (a: any, b: any) => {
      if (a?.id !== undefined && b?.id !== undefined) return a.id === b.id;
      return a === b;
    });
  }, [selectedItems]);

  const labeledItems = useMemo(
    () => getEntitiesWithPersonLabel(selectedItems, personLabelI18nKey),
    [selectedItems, personLabelI18nKey]
  );

  const integrationDataResult = useMemo(
    () => getIntegrationData(labeledItems, integrationsList, t(`${i18nNamespace}:duplicates.none`)),
    [labeledItems, integrationsList, i18nNamespace, t]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues(attributeRows, integrationsList),
    [attributeRows, integrationsList]
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues,
  });

  const watchedIntegrationSelections = useWatch({
    control: form.control,
    name: integrationsList,
  });

  // Build table data from attribute rows and integration data
  const attributeTableRows = useMemo(
    () => buildAttributeTableRows(attributeRows, selectedItems),
    [attributeRows, selectedItems]
  );

  const integrationTableRows = useMemo(
    () => buildIntegrationTableRows(integrationsList, integrationDataResult, i18nNamespace, t),
    [integrationsList, integrationDataResult, i18nNamespace, t]
  );

  const shouldShowMissingIntegrationNotice = useMemo(
    () => watchedIntegrationSelections.some(selection => selection === 'none'),
    [watchedIntegrationSelections]
  );

  const tableData = useMemo(
    () => [...attributeTableRows, ...integrationTableRows],
    [attributeTableRows, integrationTableRows]
  );

  const groupedData = useMemo(
    () =>
      integrationTableRows.length > 0
        ? [
            { category: ONBOARDING_CONFLICT_CATEGORY_ATTRIBUTES, items: attributeTableRows },
            { category: ONBOARDING_CONFLICT_CATEGORY_INTEGRATIONS, items: integrationTableRows },
          ]
        : undefined,
    [attributeTableRows, integrationTableRows]
  );

  const personLabels = useMemo(() => labeledItems.map(item => item.label), [labeledItems]);

  const mergeMutation = useMutation({
    mutationFn: mergeMutationFn,
    onSuccess: () => {
      onClose(false);
      onSuccess();
      void invalidateQueries(queryClient);
    },
    onError: (error: unknown) => {
      displayToastErrorFromApiWithFallback(error, 'common:api.error.generic');
    },
  });

  const onClose = (nextOpen: boolean) => {
    form.reset();
    setOpen(nextOpen);
  };

  const onSubmit = (data: FormValues) => {
    const formData = data as Record<string, string>;

    // Parse integrations from form data
    const integrationsToUse = integrationsList
      .map(integration => {
        const selectedValue = formData[integration];
        if (selectedValue && selectedValue !== 'none') {
          return {
            integration,
            sourceId: parseInt(selectedValue, 10),
          };
        }
        return null;
      })
      .filter(Boolean) as { integration: string; sourceId: number }[];

    const mergeRequest = mergeRequestBuilder.build(formData, selectedItems, integrationsToUse);
    mergeMutation.mutate(mergeRequest);
  };

  // Reset form with common values and integration defaults when modal opens
  useEffect(() => {
    if (open) {
      const resetValues: Record<string, string> = {};

      for (const row of attributeRows) {
        if (row.forcedDefaultValue !== undefined) {
          resetValues[row.key] = row.forcedDefaultValue;
        } else {
          const commonVal = commonValues[row.key as keyof typeof commonValues];
          let resolvedValue: string;
          // Handle nested objects (e.g. client with .id)
          if (commonVal?.id !== undefined) {
            resolvedValue = String(commonVal.id);
          } else {
            resolvedValue = commonVal ?? '';
          }
          resetValues[row.key] = row.resolveDefaultValue
            ? row.resolveDefaultValue(selectedItems, resolvedValue)
            : resolvedValue;
        }
      }

      form.reset({
        ...resetValues,
        ...integrationDataResult.defaultValues,
      });

      void form.trigger();
    }
  }, [open, commonValues, integrationDataResult, form, attributeRows, selectedItems]);

  const isTriggerDisabled = selectedItems.length < 2 || triggerDisabled;
  const triggerButton = (
    <Button type="button" size="sm" disabled={isTriggerDisabled}>
      <Blend />
      {t(`${i18nNamespace}:duplicates.resolve`)}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {isTriggerDisabled ? (
        triggerDisabled && triggerDisabledTooltip ? (
          <SimpleTooltip content={triggerDisabledTooltip}>
            <span className="inline-flex">{triggerButton}</span>
          </SimpleTooltip>
        ) : (
          triggerButton
        )
      ) : (
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      )}

      <DialogContent className="w-[100%] max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>{t(`${i18nNamespace}:duplicates.resolveTitle`)}</DialogTitle>
          <DialogDescription>
            {t(`${i18nNamespace}:duplicates.resolveDescription`)}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={e => {
            e.stopPropagation();
            form.handleSubmit(onSubmit)(e);
          }}
          style={{ padding: 0, overflow: 'hidden' }}
        >
          <OnboardingConflictsComparisonTable
            tableData={tableData}
            groupedData={groupedData}
            personLabels={personLabels}
            form={form}
            t={t}
            resultingEntityLabel={t(resultingEntityI18nKey)}
          />

          {shouldShowMissingIntegrationNotice && renderFooterNotice?.()}

          <DialogFooter className="mt-4 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t('common:cancel')}
              </Button>
            </DialogClose>
            <ButtonLoadable
              type="submit"
              isLoading={mergeMutation.isPending}
              disabled={mergeMutation.isPending || !form.formState.isValid}
            >
              {t(`${i18nNamespace}:duplicates.merge`)}
            </ButtonLoadable>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
